/**
 * RAG Service - API client for the RAG microservice
 * Provides functions for working with collections, documents, and queries
 */
import axios from "axios";
import { tokenManager } from "../utils/tokenManager";
import { withAcceptLanguageHeader } from "../utils/requestLanguage";
import { getRequestScopeHeaders } from "../utils/requestScope";
import { deploymentApi } from "./api";

const RAG_API_URL =
  window.env.REACT_APP_RAG_API_URL || "http://localhost:8002/api/v1";
const OMNIFLOW_API_BASE_URL =
  window.env.REACT_APP_RAG_API_URL || "http://localhost:8001";

// Create axios instance for RAG API
const ragApi = axios.create({
  baseURL: RAG_API_URL,
});

// Create axios instance for Omniflow API
const omniflowApi = axios.create({
  baseURL: OMNIFLOW_API_BASE_URL,
});

// Setup secure interceptors for both APIs using centralized token manager
tokenManager.setupInterceptors(ragApi);
tokenManager.setupInterceptors(omniflowApi);

// Helper function to handle response errors securely
const handleApiError = (error) => {
  let errorMessage = "";

  const extractErrorMessage = (data) => {
    if (!data) return "";

    if (typeof data === "string") return data;

    if (Array.isArray(data)) {
      return data
        .map((item) => {
          if (typeof item === "string") return item;
          if (typeof item?.msg === "string") return item.msg;
          if (typeof item?.message === "string") return item.message;
          if (typeof item?.detail === "string") return item.detail;
          return "";
        })
        .filter(Boolean)
        .join(" ");
    }

    if (typeof data?.message === "string") return data.message;
    if (typeof data?.detail === "string") return data.detail;
    if (Array.isArray(data?.detail)) return extractErrorMessage(data.detail);
    if (typeof data?.error?.message === "string") return data.error.message;
    if (typeof data?.error?.detail === "string") return data.error.detail;

    if (typeof data?.detail?.message === "string") return data.detail.message;
    if (typeof data?.error === "string") return data.error;

    return "";
  };

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    if (error.response.status === 401) {
      // Authentication error
      errorMessage = "Authentication failed. Please sign in again.";

      // Log additional debugging information
    } else if (
      error.response.status === 404 &&
      error.response.data?.detail?.missing_users
    ) {
      // Handling 404 with missing users
      errorMessage =
        error.response.data.detail.message || "Some users were not found";
      error.missingUsers = error.response.data.detail.missing_users;
      // Users not found error
    } else {
      errorMessage =
        extractErrorMessage(error.response.data) ||
        `Error ${error.response.status}: ${error.response.statusText}`;
    }
  } else if (error.request) {
    // The request was made but no response was received
    errorMessage = "No response from server. Please check your connection.";
  } else {
    // Something happened in setting up the request that triggered an Error
    errorMessage = error.message;
    throw new Error(errorMessage);
  }

  // Assign the error message
  error.message = errorMessage;
  throw error;
};

// Collections API
const collectionsApi = {
  /**
   * Get all collections with optional search
   * @param {Object} options - Search options
   * @param {string} options.search - Optional search term
   * @param {number} options.skip - Number of records to skip (pagination)
   * @param {number} options.limit - Number of records to return (pagination)
   * @param {Array} options.project_ids - Optional array of project IDs to filter by
   * @returns {Promise<Array>} List of collections
   */
  getCollections: async ({
    search = "",
    skip = 0,
    limit = 100,
    project_ids = null,
  } = {}) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (skip) params.append("skip", skip);
      if (limit) params.append("limit", limit);

      // Add project_ids parameter if provided (and not 'all')
      if (
        project_ids &&
        Array.isArray(project_ids) &&
        !project_ids.includes("all")
      ) {
        // Skip adding 'all' since that's the default behavior on the backend
        project_ids.forEach((id) => params.append("project_ids", id));
      }

      const response = await ragApi.get(`/collections/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get a specific collection by ID
   * @param {string} collectionId - Collection UUID
   * @param {Array} project_ids - Optional array of project IDs to filter by
   * @returns {Promise<Object>} Collection data with stats
   */
  getCollection: async (collectionId, project_ids = null) => {
    try {
      const params = new URLSearchParams();

      // Add project_ids parameter if provided (and not 'all')
      if (
        project_ids &&
        Array.isArray(project_ids) &&
        !project_ids.includes("all")
      ) {
        // Skip adding 'all' since that's the default behavior on the backend
        project_ids.forEach((id) => params.append("project_ids", id));
      }

      const url = params.toString()
        ? `${RAG_API_URL}/collections/${collectionId}?${params.toString()}`
        : `${RAG_API_URL}/collections/${collectionId}`;

      const response = await ragApi.get(url);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Create a new collection
   * @param {Object} collectionData - Collection data
   * @param {string} collectionData.name - Collection name
   * @param {string} collectionData.description - Optional collection description
   * @param {boolean} collectionData.is_public - Whether the collection is public
   * @param {string} collectionData.llm_config_id - Optional embedding model ID
   * @returns {Promise<Object>} Created collection data
   */
  createCollection: async (collectionData) => {
    try {
      const response = await ragApi.post("/collections/", collectionData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update an existing collection
   * @param {string} collectionId - Collection UUID
   * @param {Object} collectionData - Collection data to update
   * @returns {Promise<Object>} Updated collection data
   */
  updateCollection: async (collectionId, collectionData) => {
    try {
      const response = await ragApi.put(
        `/collections/${collectionId}`,
        collectionData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete a collection and all its documents
   * @param {string} collectionId - Collection UUID
   * @returns {Promise<void>}
   */
  deleteCollection: async (collectionId) => {
    try {
      await ragApi.delete(`/collections/${collectionId}`);
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Share a collection with another user
   * @param {string} collectionId - Collection UUID
   * @param {Object} shareData - Share data
   * @param {string} shareData.email - Email of the user to share with
   * @param {string} shareData.access_level - Access level (admin, modifier, viewer)
   * @returns {Promise<Object>} Share data with user info
   */
  shareCollection: async (collectionId, shareData) => {
    try {
      const response = await ragApi.post(
        `/collections/${collectionId}/sharing`,
        shareData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// Documents API
const documentsApi = {
  /**
   * Get documents from a specific collection
   * @param {string} collectionId - Collection UUID
   * @param {Object} options - Pagination options
   * @param {number} options.skip - Number of records to skip
   * @param {number} options.limit - Number of records to return
   * @returns {Promise<Array>} List of documents
   */
  getDocumentsByCollection: async (
    collectionId,
    { skip = 0, limit = 100 } = {},
  ) => {
    try {
      const params = new URLSearchParams();
      if (skip) params.append("skip", skip);
      if (limit) params.append("limit", limit);

      const response = await ragApi.get(
        `/documents/collection/${collectionId}?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get a specific document by ID, optionally including chunks
   * @param {string} documentId - Document UUID
   * @param {boolean} includeChunks - Whether to include document chunks
   * @returns {Promise<Object>} Document data
   */
  getDocument: async (documentId, includeChunks = false) => {
    try {
      const params = new URLSearchParams();
      if (includeChunks) params.append("include_chunks", true);

      const response = await ragApi.get(
        `/documents/${documentId}?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Upload a document to a collection
   * @param {File} file - File object to upload
   * @param {string} title - Document title
   * @param {string} collectionId - Collection UUID
   * @param {Object} metadata - Optional document metadata
   * @returns {Promise<Object>} Uploaded document data
   */
  uploadDocument: async (file, title, collectionId, metadata = null) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      formData.append("collection_id", collectionId);

      if (metadata) {
        formData.append("metadata", JSON.stringify(metadata));
      }

      const response = await ragApi.post("/documents/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Upload documents from an external source
   * @param {Object} sourceData - External source data
   * @param {string} sourceData.collection_id - Collection UUID
   * @param {string} sourceData.source_type - Type of external source (e.g., 's3')
   * @param {Object} sourceData.s3_config - S3 configuration (when source_type is 's3')
   * @returns {Promise<Object>} Response with task information
   */
  uploadFromExternalSource: async (sourceData) => {
    try {
      const response = await ragApi.post(
        "/documents/external-source",
        sourceData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update a document
   * @param {string} documentId - Document UUID
   * @param {Object} documentData - Document data to update
   * @returns {Promise<Object>} Updated document data
   */
  updateDocument: async (documentId, documentData) => {
    try {
      const response = await ragApi.put(
        `/documents/${documentId}`,
        documentData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete a document
   * @param {string} documentId - Document UUID
   * @returns {Promise<void>}
   */
  deleteDocument: async (documentId) => {
    try {
      await ragApi.delete(`/documents/${documentId}`);
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get the status of a document upload task
   * @param {string} taskId - Task UUID
   * @returns {Promise<Object>} Task status information
   */
  getTaskStatus: async (taskId) => {
    try {
      const response = await ragApi.get(`/documents/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Import documents from a single web URL
   * @param {Object} webImportData - Web import data
   * @param {string} webImportData.collection_id - Collection UUID
   * @param {string} webImportData.url - URL to import from
   * @param {number} webImportData.crawl_level - Depth of crawling (default: 1)
   * @param {number} webImportData.max_links - Maximum number of links to follow (default: 10)
   * @param {boolean} webImportData.restrict_domain - Whether to restrict to same domain (default: true)
   * @returns {Promise<Object>} Response with task information
   */
  importFromWeb: async (webImportData) => {
    try {
      const response = await ragApi.post(
        "/documents/web-import",
        webImportData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Import documents from multiple web URLs (batch import)
   * @param {Object} batchWebImportData - Batch web import data
   * @param {string} batchWebImportData.collection_id - Collection UUID
   * @param {Array<string>} batchWebImportData.urls - Array of URLs to import from
   * @param {number} batchWebImportData.crawl_level - Depth of crawling (default: 1)
   * @param {number} batchWebImportData.max_links_per_url - Maximum number of links to follow per URL (default: 10)
   * @param {boolean} batchWebImportData.restrict_domain - Whether to restrict to same domain (default: true)
   * @returns {Promise<Object>} Response with task information
   */
  batchImportFromWeb: async (batchWebImportData) => {
    try {
      const response = await ragApi.post(
        "/documents/batch-web-import",
        batchWebImportData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// Queries API
const queriesApi = {
  /**
   * Helper function to convert legacy query_text format to messages format
   * @private
   * @param {Object} queryData - Query data that might contain query_text
   * @returns {Object} Query data with messages format
   *
   * Example usage:
   * Input: { collection_id: "123", query_text: "What is AI?" }
   * Output: { collection_id: "123", messages: [{ role: "user", content: "What is AI?" }] }
   */
  _convertToMessagesFormat: (queryData) => {
    // If queryData already has messages, use it as-is
    if (queryData.messages) {
      return queryData;
    }

    // If queryData has query_text, convert it to messages format
    if (queryData.query_text) {
      const convertedData = { ...queryData };

      // Convert query_text to messages format
      convertedData.messages = [
        {
          role: "user",
          content: queryData.query_text,
        },
      ];

      // Remove the old query_text field
      delete convertedData.query_text;

      return convertedData;
    }

    // If neither messages nor query_text is provided, throw an error
    throw new Error(
      'Query data must contain either "messages" or "query_text" field',
    );
  },

  /**
   * Helper function to convert messages array to a single query text (for backward compatibility)
   * @private
   * @param {Array} messages - Array of message objects
   * @returns {string} Combined query text
   *
   * Example usage:
   * Input: [{ role: "user", content: "Hello" }, { role: "assistant", content: "Hi there!" }, { role: "user", content: "What is AI?" }]
   * Output: "What is AI?" (content of the last message)
   */
  _extractQueryTextFromMessages: (messages) => {
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      return "";
    }

    // Return the content of the last message (current user query)
    const lastMessage = messages[messages.length - 1];
    return lastMessage.content || "";
  },

  /**
   * Create a new query on a collection
   * @param {Object} queryData - Query data
   * @param {string} queryData.collection_id - Collection UUID
   * @param {Array} queryData.messages - Chat history, last message is the current user query
   * @param {string} queryData.query_text - (Legacy) Query text - will be converted to messages format
   * @param {boolean} queryData.use_agents - Whether to use agents
   * @param {Object} queryData.query_metadata - Optional query metadata
   * @param {Object} queryData.agent_config - Optional agent configuration
   * @param {string} queryData.llm_config_id - Optional LLM config ID
   * @param {Object} queryData.llm_config - Optional custom LLM config
   * @param {string} queryData.api_key - Optional API key for external services
   * @param {Object} queryData.retrieval_config - Optional retrieval configuration
   * @returns {Promise<Object>} Created query data
   */
  createQuery: async (queryData) => {
    try {
      // Convert to new messages format if needed
      const convertedData = queriesApi._convertToMessagesFormat(queryData);

      const response = await ragApi.post("/queries/", convertedData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Create a streaming query on a collection
   * @param {Object} queryData - Query data
   * @param {string} queryData.collection_id - Collection UUID
   * @param {string} queryData.flow_id - Flow UUID
   * @param {Array} queryData.messages - Chat history, last message is the current user query
   * @param {string} queryData.query_text - (Legacy) Query text - will be converted to messages format
   * @param {boolean} queryData.use_agents - Whether to use agents
   * @param {Object} queryData.llm_config - Optional LLM configuration override
   * @param {string} queryData.api_key - Optional API key for external services
   * @param {Object} queryData.retrieval_config - Optional retrieval configuration
   * @param {Function} onMessage - Callback for each message chunk or event
   * @param {Function} onDone - Callback when stream is complete
   * @param {Function} onError - Callback when error occurs
   * @returns {AbortController} Controller to abort the stream
   */
  createStreamingQuery: (queryData, onMessage, onDone, onError) => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchStream = async () => {
      try {
        // Convert to new messages format if needed
        const convertedQueryData =
          queriesApi._convertToMessagesFormat(queryData);

        // Configure endpoint and headers based on deployment type
        let endpoint;
        let headers = withAcceptLanguageHeader({
          "Content-Type": "application/json",
        });
        let requestBody;

        // Check if this is a vLLM deployment
        const isVllmDeployment =
          convertedQueryData.llm_config?.provider === "vllm" &&
          convertedQueryData.llm_config?.requires_api_key === true &&
          convertedQueryData.llm_config?.endpoint;

        if (isVllmDeployment) {
          // For vLLM deployments, use direct endpoint
          endpoint = convertedQueryData.llm_config.endpoint;
          // Using vLLM endpoint directly

          // Add API key header
          if (convertedQueryData.api_key) {
            headers["OMNIMIZE_APIKEY"] = convertedQueryData.api_key;
          }

          // Extract query text from messages for vLLM format
          const queryText = queriesApi._extractQueryTextFromMessages(
            convertedQueryData.messages,
          );

          // Create system prompt and format request
          const systemPrompt = `Answer the following question based on these retrieved documents:
${queryText}

Documents:
[Will be retrieved from collection ${convertedQueryData.collection_id}]`;

          requestBody = {
            model: "input-models",
            messages: [
              { role: "system", content: systemPrompt },
              { role: "user", content: queryText },
            ],
            stream: true,
          };

          // Add additional parameters if needed
          if (convertedQueryData.llm_config.parameters) {
            Object.assign(
              requestBody,
              convertedQueryData.llm_config.parameters,
            );
          }
        } else {
          // For regular RAG API, use standard endpoint and authentication
          let authToken = "";

          // Get auth token (Keycloak only)
          // This is now handled by the tokenManager, so no need to call updateKeycloakToken here
          // The tokenManager will handle its own refresh logic.
          // We just need to ensure the token is available in the headers.
          authToken = tokenManager.getToken();

          if (!authToken) {
            throw new Error("User is not authenticated");
          }

          endpoint = `${RAG_API_URL}/queries/stream`;
          headers["Authorization"] = `Bearer ${authToken}`;
          Object.assign(headers, getRequestScopeHeaders());
          requestBody = convertedQueryData;
        }

        // Make streaming fetch request
        const response = await fetch(endpoint, {
          method: "POST",
          headers: headers,
          body: JSON.stringify(requestBody),
          signal,
        });

        if (!response.ok) {
          throw new Error(
            `HTTP error ${response.status}: ${await response.text()}`,
          );
        }

        // Process the SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // Response state tracking
        let isCompleted = false;

        while (!isCompleted) {
          const { value, done } = await reader.read();

          if (done) {
            if (onDone && !isCompleted) {
              onDone();
              isCompleted = true;
            }
            break;
          }

          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Split on double newlines which separate SSE events
          const events = buffer.split("\n\n");
          buffer = events.pop() || ""; // Keep the last potentially incomplete event

          // Look for completion markers in the full chunk for quick detection
          if (
            chunk.includes('"event":"complete"') ||
            chunk.includes("[DONE]")
          ) {
            // Will be processed in event loop below, but note it here
          }

          // Process each complete SSE event
          for (const eventData of events) {
            // Skip empty lines and keepalive comments
            if (!eventData.trim() || eventData.startsWith(":")) continue;

            // Extract the data payload (format: "data: {json}")
            const dataMatch = eventData.match(/^data: (.+)$/m);
            if (!dataMatch) continue;

            const data = dataMatch[1].trim();

            // Handle special [DONE] marker
            if (data === "[DONE]") {
              if (onDone && !isCompleted) {
                // Fix: Create a local variable to avoid closure issues
                const shouldCallDone = !isCompleted;
                if (shouldCallDone) {
                  onDone();
                  isCompleted = true;
                }
              }
              break;
            }

            try {
              // Parse the JSON payload
              const jsonData = JSON.parse(data);

              // Handle different response formats
              if (isVllmDeployment) {
                // Process vLLM format (OpenAI-compatible)
                if (jsonData.choices && jsonData.choices.length > 0) {
                  const choice = jsonData.choices[0];

                  if (choice.delta && choice.delta.content) {
                    // Content token
                    onMessage(choice.delta.content);
                  } else if (choice.finish_reason) {
                    // Completion from OpenAI format
                    if (onDone && !isCompleted) {
                      // Fix: Create a local variable to avoid closure issues
                      const shouldFinish = !isCompleted;
                      if (shouldFinish) {
                        onDone();
                        isCompleted = true;
                      }
                    }
                  }
                }
              } else {
                // Process standard RAG API events
                if (jsonData.event) {
                  switch (jsonData.event) {
                    case "content":
                      // Text content
                      if (jsonData.text) {
                        onMessage(jsonData.text);
                      }
                      break;

                    case "log":
                    case "error":
                    case "content_start":
                    case "content_end":
                      // Pass through event data as JSON string
                      onMessage(JSON.stringify(jsonData));
                      break;

                    case "complete":
                      // Stream completion event
                      onMessage(JSON.stringify(jsonData)); // Send to UI

                      // Trigger done callback
                      if (onDone && !isCompleted) {
                        // Fix: Remove the unused completionTimeout and fix closure
                        const shouldComplete = !isCompleted;
                        if (shouldComplete) {
                          onDone();
                          isCompleted = true;
                        }
                      }
                      break;

                    default:
                      // Unknown event
                      onMessage(JSON.stringify(jsonData));
                  }
                } else if (jsonData.text) {
                  // Legacy format with direct text
                  onMessage(jsonData.text);
                } else {
                  // Unrecognized JSON format
                  onMessage(JSON.stringify(jsonData));
                }
              }
            } catch (e) {
              // Not JSON, send as raw text
              console.error("[SSE] Error parsing event data:", e);
              onMessage(data);
            }
          }
        }
      } catch (error) {
        if (error.name === "AbortError") {
        } else {
          console.error("[SSE] Stream error:", error);
          if (onError) onError(error);
        }
      }
    };

    // Start the stream immediately
    fetchStream();

    // Return controller so caller can abort if needed
    return controller;
  },

  /**
   * Get queries from a specific collection
   * @param {string} collectionId - Collection UUID
   * @param {Object} options - Pagination options
   * @param {number} options.skip - Number of records to skip
   * @param {number} options.limit - Number of records to return
   * @returns {Promise<Array>} List of queries
   */
  getQueriesByCollection: async (
    collectionId,
    { skip = 0, limit = 50 } = {},
  ) => {
    try {
      const params = new URLSearchParams();
      if (skip) params.append("skip", skip);
      if (limit) params.append("limit", limit);

      const response = await ragApi.get(
        `/queries/collection/${collectionId}?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get a specific query by ID with its results
   * @param {string} queryId - Query UUID
   * @returns {Promise<Object>} Query data with results
   */
  getQuery: async (queryId) => {
    try {
      const response = await ragApi.get(`/queries/${queryId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete a query and its results
   * @param {string} queryId - Query UUID
   * @returns {Promise<void>}
   */
  deleteQuery: async (queryId) => {
    try {
      await ragApi.delete(`/queries/${queryId}`);
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

const flowChatApi = {
  createStreamingFlowChat: (
    conversationId,
    queryData,
    onMessage,
    onDone,
    onError,
  ) => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchStream = async () => {
      try {
        // Convert to new messages format if needed
        const convertedQueryData =
          queriesApi._convertToMessagesFormat(queryData);

        // Configure endpoint and headers based on deployment type
        let endpoint;
        let headers = withAcceptLanguageHeader({
          "Content-Type": "application/json",
        });
        let requestBody;
        let authToken = "";

        // Get auth token (Keycloak only)
        // This is now handled by the tokenManager, so no need to call updateKeycloakToken here
        // The tokenManager will handle its own refresh logic.
        // We just need to ensure the token is available in the headers.
        authToken = tokenManager.getToken();

        if (!authToken) {
          throw new Error("User is not authenticated");
        }

        endpoint = `${RAG_API_URL}/conversations/${conversationId}/flowchat/stream`;
        headers["Authorization"] = `Bearer ${authToken}`;
        Object.assign(headers, getRequestScopeHeaders());
        requestBody = convertedQueryData;

        // Make streaming fetch request
        const response = await fetch(endpoint, {
          method: "POST",
          headers: headers,
          body: JSON.stringify(requestBody),
          signal,
        });

        if (!response.ok) {
          throw new Error(
            `HTTP error ${response.status}: ${await response.text()}`,
          );
        }

        // Process the SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // Response state tracking
        let isCompleted = false;

        while (!isCompleted) {
          const { value, done } = await reader.read();

          if (done) {
            if (onDone && !isCompleted) {
              onDone();
              isCompleted = true;
            }
            break;
          }

          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Split on double newlines which separate SSE events
          const events = buffer.split("\n\n");
          buffer = events.pop() || ""; // Keep the last potentially incomplete event

          // Look for completion markers in the full chunk for quick detection
          if (
            chunk.includes('"event":"complete"') ||
            chunk.includes("[DONE]")
          ) {
            // Will be processed in event loop below, but note it here
          }

          // Process each complete SSE event
          for (const eventData of events) {
            // Skip empty lines and keepalive comments
            if (!eventData.trim() || eventData.startsWith(":")) continue;

            // Extract the data payload (format: "data: {json}")
            const dataMatch = eventData.match(/^data: (.+)$/m);
            if (!dataMatch) continue;

            const data = dataMatch[1].trim();

            // Handle special [DONE] marker
            if (data === "[DONE]") {
              if (onDone && !isCompleted) {
                // Fix: Create a local variable to avoid closure issues
                const shouldCallDone = !isCompleted;
                if (shouldCallDone) {
                  onDone();
                  isCompleted = true;
                }
              }
              break;
            }

            try {
              // Parse the JSON payload
              const jsonData = JSON.parse(data);

              // Process standard RAG API events
              if (jsonData.event) {
                switch (jsonData.event) {
                  case "content":
                    // Text content
                    if (jsonData.text) {
                      onMessage(jsonData.text);
                    }
                    break;

                  case "log":
                  case "error":
                  case "content_start":
                  case "content_end":
                    // Pass through event data as JSON string
                    onMessage(JSON.stringify(jsonData));
                    break;

                  case "complete":
                    // Stream completion event
                    onMessage(JSON.stringify(jsonData)); // Send to UI

                    // Trigger done callback
                    if (onDone && !isCompleted) {
                      // Fix: Remove the unused completionTimeout and fix closure
                      const shouldComplete = !isCompleted;
                      if (shouldComplete) {
                        onDone();
                        isCompleted = true;
                      }
                    }
                    break;

                  default:
                    // Unknown event
                    onMessage(JSON.stringify(jsonData));
                }
              } else if (jsonData.text) {
                // Legacy format with direct text
                onMessage(jsonData.text);
              } else {
                // Unrecognized JSON format
                onMessage(JSON.stringify(jsonData));
              }
            } catch (e) {
              // Not JSON, send as raw text
              console.error("[SSE] Error parsing event data:", e);
              onMessage(data);
            }
          }
        }
      } catch (error) {
        if (error.name === "AbortError") {
        } else {
          console.error("[SSE] Stream error:", error);
          if (onError) onError(error);
        }
      }
    };

    // Start the stream immediately
    fetchStream();

    // Return controller so caller can abort if needed
    return controller;
  },
};

// LLM API
const llmApi = {
  /**
   * Get all supported LLM providers
   * @param {Object} options - Optional filter parameters
   * @param {boolean} options.is_active - Filter by active status
   * @param {boolean} options.supports_embedding - Filter by embedding support
   * @param {boolean} options.supports_generation - Filter by generation support
   * @returns {Promise<Array>} List of providers
   */
  getProviders: async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.is_active !== undefined)
        params.append("is_active", options.is_active);
      if (options.supports_embedding !== undefined)
        params.append("supports_embedding", options.supports_embedding);
      if (options.supports_generation !== undefined)
        params.append("supports_generation", options.supports_generation);

      const response = await ragApi.get(
        `/llm-configs/providers?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  /**
   * @param {{organizationId: string | null, projectId: string | null}} [requestScope]
   */
  getDefaultModels: async (requestScope) => {
    try {
      const response = requestScope
        ? await ragApi.get("/model-defaults", { requestScope })
        : await ragApi.get("/model-defaults");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Generate a chat response from a conversation
   * @param {Object} chatRequest - Chat request data
   * @param {Array} chatRequest.messages - Array of message objects with role and content
   * @param {Object} chatRequest.config - LLM configuration
   * @returns {Promise<Object>} Generated chat response
   */
  generateChatResponse: async (chatRequest) => {
    try {
      const response = await ragApi.post("/llm/chat/", chatRequest);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Generate a streaming chat response from a conversation
   * @param {Object} chatRequest - Chat request data
   * @param {Array} chatRequest.messages - Array of message objects with role and content
   * @param {Object} chatRequest.config - LLM configuration
   * @param {Function} onContent - Callback for content chunks
   * @param {Function} onEvent - Callback for all events (content_start, content_end, log, error)
   * @param {Function} onComplete - Callback when stream is complete
   * @param {Function} onError - Callback when error occurs
   * @returns {AbortController} Controller to abort the stream
   */
  generateStreamingChatResponse: (
    chatRequest,
    onContent,
    onEvent,
    onError,
    onComplete,
  ) => {
    const controller = new AbortController();
    const { signal } = controller;

    // Send content_start event immediately to show typing animation
    if (onEvent) {
      onEvent({ event: "content_start" });
    }

    const fetchStream = async () => {
      try {
        // Check if this is a vLLM deployment
        const isVllmDeployment =
          chatRequest.config?.provider === "vllm" &&
          chatRequest.config?.requires_api_key === true &&
          chatRequest.config?.endpoint;

        if (isVllmDeployment) {
        }

        // Format request differently for vLLM deployments
        let endpoint;
        let headers = withAcceptLanguageHeader({
          "Content-Type": "application/json",
        });
        let requestBody;

        if (isVllmDeployment) {
          // For vLLM deployments, use direct endpoint and special formatting
          endpoint = chatRequest.config.endpoint;

          // Add OMNIMIZE_APIKEY header for vLLM
          if (chatRequest.api_key) {
            headers["OMNIMIZE_APIKEY"] = chatRequest.api_key;
          } else {
            console.warn("No API key provided for vLLM deployment");
          }

          // Format request for vLLM
          requestBody = {
            model: "input-models", // Always use "input-models" as model parameter
            messages: chatRequest.messages,
            stream: true,
          };

          // Add any additional parameters if needed
          if (chatRequest.config.parameters) {
            Object.assign(requestBody, chatRequest.config.parameters);
          }
        } else {
          // Get auth token using Keycloak
          let authToken = "";

          // Get authentication token from Keycloak
          // This is now handled by the tokenManager, so no need to call updateKeycloakToken here
          // The tokenManager will handle its own refresh logic.
          // We just need to ensure the token is available in the headers.
          authToken = tokenManager.getToken();

          if (!authToken) {
            throw new Error("User is not authenticated");
          }

          // For regular LLM API, use standard endpoint and authentication
          endpoint = `${RAG_API_URL}/llm/chat/stream`;
          headers["Authorization"] = `Bearer ${authToken}`;
          Object.assign(headers, getRequestScopeHeaders());
          requestBody = chatRequest;
        }

        // Make streaming fetch request
        const response = await fetch(endpoint, {
          method: "POST",
          headers: headers,
          body: JSON.stringify(requestBody),
          signal,
        });

        if (!response.ok) {
          // Handle HTTP errors
          if (response.status === 401) {
            throw new Error("Authentication failed. Please sign in again.");
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `HTTP error ${response.status}`);
        }

        // Set up SSE reader with direct access to the reader for more responsive streaming
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // Process the stream
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // Process any remaining content in the buffer
            if (buffer.trim()) {
              const dataMatch = buffer.match(/^data: (.+)$/m);
              if (dataMatch) {
                try {
                  const data = dataMatch[1].trim();
                  processEvent(data, isVllmDeployment);
                } catch (e) {
                  console.error("Error processing final buffer data:", e);
                }
              }
            }

            // Send content_end event
            if (onEvent) {
              onEvent({ event: "content_end" });
            }

            if (onComplete) onComplete();
            break;
          }

          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete SSE events (each event is separated by double newlines)
          const lines = buffer.split("\n\n");

          // Keep the last (possibly incomplete) line in the buffer
          buffer = lines.pop() || "";

          // Process each complete event immediately to ensure tokens are displayed in real-time
          for (const line of lines) {
            if (!line.trim() || line.startsWith(":")) continue; // Skip empty lines and comments

            const dataMatch = line.match(/^data: (.+)$/m);
            if (dataMatch) {
              try {
                const data = dataMatch[1].trim();
                processEvent(data, isVllmDeployment);
              } catch (e) {
                console.error("Error processing event:", e, line);
              }
            }
          }
        }
      } catch (error) {
        if (error.name === "AbortError") {
          // Stream was aborted intentionally
        } else {
          if (onError) onError(error);
        }
      }
    };

    // Process an individual event from the stream
    const processEvent = (data, isVllmDeployment) => {
      // Check for [DONE] signal
      if (data === "[DONE]") {
        if (onEvent) {
          onEvent({ event: "complete" });
        }
        if (onComplete) onComplete();
        return;
      }

      try {
        // Parse JSON data
        const jsonData = JSON.parse(data);

        // Handle different response formats
        if (isVllmDeployment) {
          // Process vLLM response format which follows OpenAI format
          if (jsonData.choices && jsonData.choices.length > 0) {
            const choice = jsonData.choices[0];

            if (choice.delta && choice.delta.content) {
              // Extract the content from the delta
              if (onContent) {
                onContent(choice.delta.content);
              }
            } else if (choice.finish_reason) {
              // Completion event from OpenAI format
              if (onEvent) {
                onEvent({ event: "complete" });
              }
              if (onComplete) {
                onComplete();
              }
            }
          } else {
            // Log unexpected vLLM response format for debugging
            console.warn("Unexpected vLLM response format:", jsonData);
          }
        } else {
          // Handle standard RAG API response format
          if (jsonData.event === "content" && jsonData.text) {
            // Send content token immediately
            if (onContent) {
              onContent(jsonData.text);
            }
          } else if (
            jsonData.event === "content_start" ||
            jsonData.event === "content_end" ||
            jsonData.event === "log" ||
            jsonData.event === "error" ||
            jsonData.event === "complete"
          ) {
            // Send other events
            if (onEvent) {
              onEvent(jsonData);
            }

            // If it's a completion event, also trigger onComplete
            if (jsonData.event === "complete" && onComplete) {
              onComplete();
            }

            // If it's an error event, also trigger onError
            if (jsonData.event === "error" && onError) {
              onError(new Error(jsonData.message));
            }
          }
        }
      } catch (err) {
        // If it's not valid JSON, treat as plain text content
        if (onContent && data) {
          onContent(data);
        }
      }
    };

    // Start the stream immediately
    fetchStream();

    // Return controller so caller can abort if needed
    return controller;
  },

  /**
   * Get available LLM configurations
   * @param {Object} options - Optional filter parameters
   * @param {number} options.page - Current page number (1-based)
   * @param {number} options.limit - Number of items per page
   * @param {boolean} options.is_active - Filter by active status
   * @param {boolean} options.is_embedding_model - Filter by embedding model status
   * @param {boolean} options.is_stt_model - Filter by STT model status
   * @param {string} options.provider - Filter by provider
   * @param {Array} options.project_ids - Optional array of project IDs to filter by
   * @param {boolean} options.include_public - Whether to include public models
   * @param {boolean} options.include_shared - Whether to include models shared with the user
   * @param {{organizationId: string | null, projectId: string | null}} [requestScope]
   * @returns {Promise<Array>} List of LLM configurations
   */
  getLLMConfigs: async (options = {}, requestScope) => {
    try {
      const params = new URLSearchParams();
      if (Number(options.page) >= 0)
        params.append("skip", Number(options.page) * Number(options.limit));
      if (options.limit) params.append("limit", options.limit);
      if (options.name) params.append("name", options.name);
      if (options.used_only) params.append("used_only", options.used_only);
      if (options.is_active !== undefined)
        params.append("is_active", options.is_active);
      if (options.is_embedding_model !== undefined)
        params.append("is_embedding_model", options.is_embedding_model);
      if (options.is_stt_model !== undefined)
        params.append("is_stt_model", options.is_stt_model);
      if (options.provider) params.append("provider", options.provider);
      if (options.include_public !== undefined)
        params.append("include_public", options.include_public);
      if (options.include_shared !== undefined)
        params.append("include_shared", options.include_shared);
      if (options.type !== undefined) params.append("type", options.type);

      // Add project_ids parameter if provided (and not 'all')
      if (
        options.project_ids &&
        Array.isArray(options.project_ids) &&
        !options.project_ids.includes("all")
      ) {
        // Skip adding 'all' since that's the default behavior on the backend
        options.project_ids.forEach((id) => params.append("project_ids", id));
      }

      const url = `/llm-configs/?${params.toString()}`;
      const response = requestScope
        ? await ragApi.get(url, { requestScope })
        : await ragApi.get(url);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get LLM configuration by external ID
   * @param {string} externalId - External ID to filter by (deployment ID)
   * @returns {Promise<Object|null>} LLM configuration or null if not found
   */
  getLLMConfigByExternalId: async (externalId) => {
    try {
      const params = new URLSearchParams();
      params.append("external_id", externalId);

      const response = await ragApi.get(`/llm-configs/?${params.toString()}`);
      // Return the first matching configuration or null if none found
      return response.data.length > 0 ? response.data[0] : null;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Create a new LLM configuration
   * @param {Object} configData - LLM configuration data
   * @returns {Promise<Object>} Created LLM configuration
   */
  createLLMConfig: async (configData) => {
    try {
      const response = await ragApi.post("/llm-configs/user", configData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update an existing LLM configuration
   * @param {string} configId - LLM configuration UUID
   * @param {Object} configData - LLM configuration data
   * @returns {Promise<Object>} Updated LLM configuration
   */
  updateLLMConfig: async (configId, configData) => {
    try {
      const response = await ragApi.patch(
        `/llm-configs/${configId}`,
        configData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete an LLM configuration
   * @param {string} configId - LLM configuration UUID
   * @returns {Promise<void>}
   */
  deleteLLMConfig: async (configId) => {
    try {
      await ragApi.delete(`/llm-configs/${configId}`);
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get an LLM configuration
   * @param {string} configId - LLM configuration UUID
   * @returns {Promise<void>}
   */
  getLLMConfig: async (configId) => {
    try {
      const response = await ragApi.get(`/llm-configs/${configId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Share an LLM configuration with another user
   * @param {string} configId - LLM configuration UUID
   * @param {Object} shareData - Share data
   * @param {string} shareData.email - Email of the user to share with
   * @param {string} shareData.access_level - Access level (admin, modifier, viewer)
   * @returns {Promise<Object>} Share data with user info
   */
  shareLLMConfig: async (configId, shareData) => {
    try {
      const response = await ragApi.post(
        `/llm-configs/${configId}/sharing`,
        shareData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get the list of users an LLM configuration is shared with
   * @param {string} configId - LLM configuration UUID
   * @returns {Promise<Array>} List of share data with user info
   */
  getLLMConfigShares: async (configId) => {
    try {
      const response = await ragApi.get(`/llm-configs/${configId}/sharing`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Remove a user from a shared LLM configuration
   * @param {string} configId - LLM configuration UUID
   * @param {string} userId - ID of the user to remove
   * @returns {Promise<boolean>}
   */
  unshareByUserId: async (configId, userId) => {
    try {
      await ragApi.delete(
        `/llm-configs/${configId}/sharing?user=${encodeURIComponent(userId)}`,
      );

      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Remove a user from a shared LLM configuration (unshare)
   * @param {string} configId - LLM configuration UUID
   * @param {string} email - Email of the user to remove from sharing
   * @returns {Promise<void>}
   */
  unshareByEmail: async (configId, email) => {
    try {
      await ragApi.delete(`/llm-configs/${configId}/sharing/by-email/${email}`);
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Create a new user-specific LLM configuration
   * @param {Object} configData - LLM configuration data
   * @param {string} configData.name - Display name
   * @param {string} configData.provider - Provider (always 'private' for user configs)
   * @param {string} configData.model - Model identifier
   * @param {number} configData.temperature - Default temperature (0-1)
   * @param {number} configData.max_tokens - Default token limit (0 = no limit)
   * @param {Object} configData.extra_config - Extra configuration options, including API key
   * @param {string} configData.endpoint - Model endpoint URL
   * @param {boolean} configData.api_key_required - Whether an API key is required
   * @param {string} configData.auth_header_type - Auth header type (e.g., 'OMNIMIZE_APIKEY')
   * @param {boolean} configData.is_embedding_model - Whether this is an embedding model
   * @param {boolean} configData.is_stt_model - Whether this is an STT model
   * @param {number} configData.embedding_dimension - Dimension size for embedding models
   * @param {boolean} configData.is_default - Whether to set as default model
   * @returns {Promise<Object>} Created user LLM configuration
   */
  createUserLLMConfig: async (configData) => {
    try {
      const response = await ragApi.post("/llm-configs/user", configData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Create a new LLM configuration (admin only)
   * @param {Object} configData - LLM configuration data
   * @param {string} configData.provider - Provider name
   * @param {string} configData.model - Model name
   * @param {string} configData.name - Display name
   * @param {number} configData.temperature - Default temperature (0-1)
   * @param {number} configData.max_tokens - Default token limit (0 = no limit)
   * @param {Object} configData.extra_config - Extra configuration options, including API key
   * @param {string} configData.endpoint - Model endpoint URL
   * @param {boolean} configData.api_key_required - Whether an API key is required
   * @param {string} configData.auth_header_type - Auth header type (e.g., 'OMNIMIZE_APIKEY')
   * @param {boolean} configData.is_embedding_model - Whether this is an embedding model
   * @param {boolean} configData.is_stt_model - Whether this is an STT model
   * @param {number} configData.embedding_dimension - Dimension size for embedding models
   * @param {boolean} configData.is_default - Whether to set as default model
   * @param {boolean} configData.is_public - Whether the model is public (admin only)
   * @returns {Promise<Object>} Created LLM configuration
   */
  createAdminLLMConfig: async (configData) => {
    try {
      const response = await ragApi.post("/llm-configs/", configData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// Projects API
const projectsApi = {
  /**
   * Get all projects owned by the user, optionally including shared projects
   * @param {Object} [options] - Query options
   * @param {number} [options.skip] - Number of records to skip (pagination)
   * @param {number} [options.limit] - Number of records to return (pagination)
   * @param {boolean} [options.includeShared] - Whether to include projects shared with the user
   * @param {boolean} [options.includeDeleted] - Whether to include soft-deleted projects
   * @param {{organizationId?: string | null, projectId?: string | null}} [options.requestScope] - Explicit request scope override
   * @returns {Promise<Array>} List of projects with stats
   */
  getProjects: async ({
    skip = 0,
    limit = 100,
    includeShared = true,
    includeDeleted = false,
    requestScope,
  } = {}) => {
    try {
      const params = new URLSearchParams();
      if (skip) params.append("skip", skip);
      if (limit) params.append("limit", limit);
      params.append("include_shared", includeShared);
      params.append("include_deleted", includeDeleted);

      // Making projects API request with valid token
      const response = await ragApi.get(`/projects/?${params.toString()}`, {
        requestScope,
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get a specific project by ID
   * @param {string} projectId - Project UUID
   * @param {boolean} includeSharingInfo - Whether to include sharing information
   * @returns {Promise<Object>} Project data with sharing information
   */
  getProject: async (projectId, includeSharingInfo = true, config = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("include_sharing_info", includeSharingInfo);

      const response = await ragApi.get(
        `/projects/${projectId}?${params.toString()}`,
        config,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @param {string} projectData.name - Project name
   * @param {string} [projectData.description] - Optional project description
   * @param {string[]} [projectData.list_share_users] - Emails to invite. Sent by
   *   ProjectContext.createProject but previously undocumented, so checkJs read
   *   it as an unknown property.
   * @param {{organizationId?: string | null, projectId?: string | null}} [requestScope] - Explicit request scope override
   * @returns {Promise<Object>} Created project data
   */
  createProject: async (projectData, requestScope) => {
    try {
      const response =
        requestScope === undefined
          ? await ragApi.post("/projects/", projectData)
          : await ragApi.post("/projects/", projectData, { requestScope });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update an existing project
   * @param {string} projectId - Project UUID
   * @param {Object} projectData - Project data to update
   * @param {string} projectData.name - Updated project name
   * @param {string} projectData.description - Updated project description
   * @param {{organizationId?: string | null, projectId?: string | null}} [requestScope] - Explicit request scope override
   * @returns {Promise<Object>} Updated project data
   */
  updateProject: async (projectId, projectData, requestScope) => {
    try {
      const response =
        requestScope === undefined
          ? await ragApi.put(`/projects/${projectId}`, projectData)
          : await ragApi.put(`/projects/${projectId}`, projectData, {
              requestScope,
            });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete a project
   * @param {string} projectId - Project UUID
   * @param {boolean} permanent - Whether to permanently delete the project
   * @param {boolean} force - Whether to force deletion even if dependencies exist
   * @param {{organizationId?: string | null, projectId?: string | null}} [requestScope] - Explicit request scope override
   * @returns {Promise<boolean>}
   */
  deleteProject: async (
    projectId,
    permanent = false,
    force = false,
    requestScope,
  ) => {
    try {
      const params = new URLSearchParams();
      if (permanent) params.append("permanent", true);
      if (force) params.append("force", "true");

      const url = `/projects/${projectId}?${params.toString()}`;
      if (requestScope === undefined) {
        await ragApi.delete(url);
      } else {
        await ragApi.delete(url, { requestScope });
      }
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Check project dependencies
   * @param {string} projectId - Project UUID
   * @returns {Promise<Object>} Dependencies information
   */
  getProjectDependencies: async (projectId) => {
    try {
      const response = await ragApi.get(`/projects/${projectId}/dependencies`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Share a project with another user
   * @param {string} projectId - Project UUID
   * @param {Object} shareData - Share data
   * @param {string} shareData.email - Email of the user to share with
   * @param {string} shareData.access_level - Access level (admin, editor, viewer)
   * @returns {Promise<Object>} Share data with user info
   */
  shareProject: async (projectId, shareData) => {
    try {
      const response = await ragApi.post(
        `/projects/${projectId}/share`,
        shareData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update access level for a shared project
   * @param {string} projectId - Project UUID
   * @param {string} shareId - Share UUID
   * @param {Object} shareData - Share data
   * @param {string} shareData.access_level - New access level (admin, editor, viewer)
   * @returns {Promise<Object>} Updated share data with user info
   */
  updateProjectShare: async (projectId, shareId, shareData) => {
    try {
      const response = await ragApi.put(
        `/projects/${projectId}/share/${shareId}`,
        shareData,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Remove a user's access to a project
   * @param {string} projectId - Project UUID
   * @param {string} shareId - Share UUID
   * @returns {Promise<void>}
   */
  deleteProjectShare: async (projectId, shareId) => {
    try {
      await ragApi.delete(`/projects/${projectId}/share/${shareId}`);
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get all team members for a specific project
   * @param {string} projectId - Project UUID
   * @returns {Promise<Array>} List of team members with access information
   */
  getProjectMembersLegacy: async (projectId) => {
    try {
      // Use getProject which already has token validation
      const project = await projectsApi.getProject(projectId, true);
      return project.sharing || [];
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get user ID from an email address
   * @param {string} email - Email address of the user
   * @returns {Promise<string|null>} User ID if found, null otherwise
   */
  getUserIdByEmail: async (email) => {
    try {
      const response = await ragApi.get(
        `/users/lookup?email=${encodeURIComponent(email)}`,
      );
      return response.data.id || null;
    } catch (error) {
      // Error looking up user by email
      return handleApiError(error);
    }
  },

  /**
   * Check if a user is a member of a project
   * @param {string} projectId - Project UUID
   * @param {string} email - Email address of the user
   * @returns {Promise<Object>} Object containing isMember and accessLevel
   */
  checkUserProjectMembership: async (projectId, email) => {
    try {
      // Use email as a query parameter
      const response = await ragApi.get(
        `/projects/${projectId}/is-member?email=${encodeURIComponent(email)}`,
      );
      return response.data; // Assuming the response format is { is_member: boolean, access_level: string | null }
    } catch (error) {
      // Handle 404 as 'user not found' / 'not a member' specifically?
      // For now, let the general error handler manage it.
      return handleApiError(error);
    }
  },

  /**
   * Get all members of a project with pagination
   * @param {string} projectId - Project UUID
   * @param {Object} options - Pagination options
   * @param {number} options.page - Current page number (1-based)
   * @param {number} options.limit - Number of items per page
   * @returns {Promise<Object>} Paginated list of project members
   */
  getProjectMembers: async (projectId, { page = 1, limit = 10 } = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);

      const response = await ragApi.get(
        `/projects/${projectId}/members?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

const flowsApi = {
  getFlowSummaries: async ({ projectIds, name, status, view, limit, cursor, signal }) => {
    const params = new URLSearchParams();
    projectIds.forEach((id) => params.append("project_ids", id));
    params.set("limit", String(limit));
    params.set("view", view);
    if (name) params.set("name", name);
    if (status !== "all") params.set("status", status);
    if (cursor) params.set("cursor", cursor);
    const response = await omniflowApi.get("/omniflow/flows/summaries", { params, signal });
    return response.data;
  },

  /**
   * Get all flows for a project
   * @param {Object} options - Optional filter parameters
   * @param {string} options.projectId - Project ID
   * @param {string} options.project_ids - Project IDs
   * @param {string} options.name - Flow name
   * @param {boolean} options.include_shared - Include shared flows
   * @returns {Promise<Array>} List of flows
   */
  getFlows: async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.projectId !== undefined)
        params.append("projectId", options.projectId);
      if (options.project_ids !== undefined)
        params.append("project_ids", options.project_ids);
      if (options.name !== undefined) params.append("name", options.name);
      if (options.include_shared !== undefined)
        params.append("include_shared", options.include_shared);
      if (options.executed_only !== undefined)
        params.append("executed_only", options.executed_only);

      const response = await omniflowApi.get(
        `/omniflow/flows?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get a single flow by its (local) id. The response's own top-level `id` field
   * is the flow's REAL AP-side id (rag-agents' route resolves the local id
   * internally and returns AP's raw response) - needed by the iframe builder,
   * which addresses flows by their AP id, not the local one.
   * @param {string} flowId - The local flow id (route param)
   * @returns {Promise<Object>} The flow, including its real AP id as `id`
   */
  getFlow: async (flowId, config = {}) => {
    try {
      const response = await omniflowApi.get(`/omniflow/flows/${flowId}`, config);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get flow runs across one or more projects.
   *
   * `cursor` is one opaque string the backend hands back as `next`; pass it
   * through unchanged to get the following page. It encodes a position per
   * underlying Activepieces account, so several projects sharing one account
   * still cost a single backend call - the caller never sees that detail.
   *
   * @param {Object} options - Filter parameters
   * @param {string[]} options.projectIds - Project IDs to include
   * @param {string} options.projectId - Single project ID (legacy shape)
   * @param {string} options.flowId - Restrict to one flow's runs
   * @param {string[]} options.status - Run statuses to include, uppercase
   * @param {string} options.cursor - Opaque pagination cursor
   * @param {number} options.limit - Page size
   * @returns {Promise<Object>} Flow runs data with pagination
   */
  getFlowRuns: async ({
    projectIds,
    projectId,
    flowId,
    status,
    cursor = "",
    limit = 10,
  }) => {
    try {
      const resolvedProjectIds = Array.isArray(projectIds)
        ? projectIds.filter(Boolean)
        : projectId
          ? [projectId]
          : [];

      if (resolvedProjectIds.length === 0 && !flowId) {
        throw new Error(
          "At least one project ID, or a flow ID, is required to fetch flow runs",
        );
      }

      const params = new URLSearchParams();
      // Repeated `project_ids=` params, which is what FastAPI's List[str]
      // query parsing expects natively (the backend also tolerates a
      // comma-joined value, but that is its fallback, not its primary shape).
      resolvedProjectIds.forEach((id) => params.append("project_ids", id));
      if (flowId) params.append("flowId", flowId);
      (status || []).forEach((value) => params.append("status", value));
      if (cursor) params.append("cursor", cursor);
      if (limit) params.append("limit", limit);

      const response = await omniflowApi.get(
        `/omniflow/flow-runs?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      // Error fetching flow runs
      return handleApiError(error);
    }
  },

  /**
   * Create a new flow
   * @param {Object} flowData - Flow data
   * @returns {Promise<Object>} Created flow data
   */
  createFlow: async (flowData) => {
    try {
      const response = await omniflowApi.post("/omniflow/flows", flowData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  deleteFlow: async (flowId) => {
    try {
      const response = await omniflowApi.delete(`/omniflow/flows/${flowId}`);
      // Flow deleted
      return response.data;
    } catch (error) {
      // Error deleting flow
      return handleApiError(error);
    }
  },

  /**
   * Toggle flow status (enable/disable)
   * @param {string} flowId - Flow ID
   * @param {object} payload - The request body (should include type and request.status)
   * @returns {Promise<Object>} Updated flow data
   */
  toggleFlowStatus: async (flowId, payload) => {
    try {
      const response = await omniflowApi.post(
        `/omniflow/flows/${flowId}`,
        payload,
      );
      return response.data;
    } catch (error) {
      // Error toggling flow status
      return handleApiError(error);
    }
  },
};

// Explore tab: Activepieces' official template gallery, mirrored/cached on the
// rag-agents side (see app/api/v1/omniflow/explore.py) so browsing templates
// never hits cloud.activepieces.com directly from the browser.
const exploreApi = {
  listTemplates: async () => {
    try {
      const response = await omniflowApi.get("/omniflow/explore");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  getCategories: async () => {
    try {
      const response = await omniflowApi.get("/omniflow/explore/categories");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  getTemplate: async (templateId) => {
    try {
      const response = await omniflowApi.get(
        `/omniflow/explore/${templateId}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  useTemplate: async (templateId, { display_name, project_id }) => {
    try {
      const response = await omniflowApi.post(
        `/omniflow/explore/${templateId}/use`,
        { display_name, project_id },
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// Usage Monitoring API
const usageMonitoringApi = {
  getUsageBalance: async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.user_id) params.append("user_id", options.user_id);
      const response = await ragApi.get(`/usage/balance/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  getUsagePlansUser: async () => {
    try {
      const response = await ragApi.get(`/usage/plans/user`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get token usage monitoring data from RAG agents
   * @param {Object} options - Optional filter parameters
   * @param {string} options.userId - (Admin only) User ID to filter by
   * @param {string} options.model - Model name to filter by
   * @param {string} options.provider - Provider name to filter by
   * @param {Date} options.start_date - Start date for filtering
   * @param {Date} options.end_date - End date for filtering
   * @returns {Promise<Object>} Usage metrics with summary and request logs
   */
  getUsageMonitoring: async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.model) params.append("model", options.model);
      if (options.provider) params.append("provider", options.provider);
      if (options.start_date)
        params.append("start_date", options.start_date.toISOString());
      if (options.end_date)
        params.append("end_date", options.end_date.toISOString());
      if (options.model_id) params.append("model", options.model_id);
      if (options.pageSize) params.append("page_size", options.pageSize);
      if (options.page) params.append("page", options.page);
      if (options.userId) params.append("user_id", options.userId); // Add user_id if provided

      const response = await ragApi.get(
        `/usage-monitoring/?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  getOmniflowUsageMonitoring: async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.model) params.append("model", options.model);
      if (options.provider) params.append("provider", options.provider);
      if (options.start_date)
        params.append("start_date", options.start_date.toISOString());
      if (options.end_date)
        params.append("end_date", options.end_date.toISOString());
      if (options.flow_id) params.append("flow_id", options.flow_id);
      if (options.pageSize) params.append("page_size", options.pageSize);
      if (options.page) params.append("page", options.page);
      if (options.userId) params.append("user_id", options.userId); // Add user_id if provided

      const response = await ragApi.get(
        `/omniflow/usage-monitoring?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get token usage summary for dashboard cards
   * @param {Object} options - Optional filter parameters
   * @returns {Promise<Object>} Usage summary data
   */
  getUsageSummary: async (options = {}) => {
    try {
      const usageData = await usageMonitoringApi.getUsageMonitoring(options);
      return {
        data: {
          total_tokens: usageData.summary?.total_tokens || 0,
          total_requests: usageData.summary?.total_requests || 0,
          total_cost: usageData.summary?.total_cost || 0,
          start_date: usageData.summary?.start_date,
          end_date: usageData.summary?.end_date,
          request_logs: usageData.request_logs || [],
        },
      };
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// AP-sync: provisions/refreshes a real, per-human Activepieces session.
// Deliberately does not go through handleApiError - callers (apTokenManager.refresh)
// need the raw rejection to distinguish success/failure.
const apSyncApi = {
  // realProjectId: the REAL rag-agents Project.id (a UUID) the caller is opening
  // the builder for - distinct from the AP-side projectId this route returns.
  // Optional; when provided, the response also carries a signed contextToken
  // (see ap_context_token.py) scoping the embedded builder to this exact project
  // and the caller's real role on it.
  getApSession: async (realProjectId, config = {}) => {
    const response = await ragApi.post(
      "/ap-sync/ap-session",
      { projectId: realProjectId ?? null },
      config,
    );
    return {
      token: response.data.token,
      projectId: response.data.projectId,
      contextToken: response.data.contextToken ?? null,
    };
  },
};

// Export all APIs individually
export {
  collectionsApi,
  documentsApi,
  queriesApi,
  llmApi,
  deploymentApi,
  projectsApi,
  flowsApi,
  flowChatApi,
  usageMonitoringApi,
  apSyncApi,
  exploreApi,
};

// Re-export the centralized token management
export { tokenManager } from "../utils/tokenManager";

// Export as a unified service
const ragService = {
  collections: collectionsApi,
  documents: documentsApi,
  queries: queriesApi,
  llm: llmApi,
  deployments: deploymentApi,
  projects: projectsApi,
  flows: flowsApi,
  flowChat: flowChatApi,
  usageMonitoring: usageMonitoringApi,
  tokenManager: tokenManager,
};

export default ragService;
